import {
  highIncomeCountries,
  lowIncomeCountries,
  lowerMiddleIncomeCountries,
  onlineNewsSources,
  socialMedia,
  upperMidIncomeCountries,
} from '@/assets/messages';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { BarChartData } from '@/components/bar-chart/toggled';
import { BarChartItem } from '@/components/bar-chart/vertical';
import { Label } from '@/components/label';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import Toggle from '@/components/toggle';
import { styled } from '@/styles/stitches.config';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useMemo, useState } from 'react';

type MediaSources = {
  social_media: string;
  online_news: string;
};

const getMediaSourceLabel = (i18n: I18n, key: keyof MediaSources) => {
  switch (key) {
    case 'social_media':
      return socialMedia(i18n);
    case 'online_news':
      return onlineNewsSources(i18n);
  }
};

const getCountriesIncomeLabel = (i18n: I18n, key: CountryIncomeCode) => {
  switch (key) {
    case 'hic':
      return highIncomeCountries(i18n);
    case 'umc':
      return upperMidIncomeCountries(i18n);
    case 'lmc':
      return lowerMiddleIncomeCountries(i18n);
    case 'lic':
      return lowIncomeCountries(i18n);
  }
};

const ChartWrapper = styled('div', {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1rem',

  '@bp4': {
    alignItems: 'flex-start',
  },
});

export function GoToSources2023Chapter4({
  data,
}: {
  data: GoToSourcesData['primary_sources_for_young_people_chart_ready'];
}) {
  const formatPercentage = useNumberFormatting('percent');
  const { i18n } = useLingui();

  const processedChartData: BarChartData[] = useMemo(() => {
    return Object.entries(data).map(([mediaSource, countyData]) => ({
      id: mediaSource,
      label: getMediaSourceLabel(i18n, mediaSource as keyof MediaSources),
      data: Object.entries(countyData).map(([countryIncomeCode, value]) => ({
        label: getCountriesIncomeLabel(i18n, countryIncomeCode as CountryIncomeCode),
        value,
      })),
    }));
  }, [data, i18n]);

  const [chartId, setChartId] = useState<keyof MediaSources>('social_media');
  const [chartData, setChartData] = useState<BarChartItem[]>(
    processedChartData[0].data as BarChartItem[]
  );

  const handleToggle = useCallback(
    (id: string) => {
      const mediaSource = id as keyof MediaSources;
      const chartData = processedChartData.find((d) => d.id === mediaSource)?.data;
      if (chartData) {
        setChartId(mediaSource);
        setChartData(chartData);
      }
    },
    [processedChartData]
  );

  return (
    <>
      <ScrollRoot>
        <ScrollGraphic>
          <ChartWrapper>
            <Label
              css={{
                color: 'var(--color-text-light)',
                fontWeight: '$medium',
                marginBlockEnd: '$1',
                maxWidth: '60ch',
                '@bp4': {
                  marginInlineStart: '1rem',
                },
              }}
            >
              <Trans id="go-to-sources-2023.chapter-3.chart-label">
                % of young people who rely primarily on the following sources of news:
              </Trans>
            </Label>
            <Toggle
              css={{
                marginBlockEnd: '$5',
                '@bp4': {
                  marginInlineStart: '1rem',
                },
              }}
              selectedId={chartId}
              items={[
                { id: 'social_media', label: 'Social media' },
                { id: 'online_news', label: 'Online news sources' },
              ]}
              onChange={handleToggle}
            />
            <HorizontalBarChart
              label={t({
                id: 'go-to-sources-2023.chapter-3.chart-label',
                message: '% of young people who rely primarily on the following sources of news:',
              })}
              data={chartData}
              formatValue={formatPercentage}
              hasAlternatingColors
              hasSeparateLabel
            />
          </ChartWrapper>
        </ScrollGraphic>

        <ScrollSteps offset={0.5} hasGraphic>
          <ScrollStep>
            <StoryText>
              <Trans id="go-to-sources-2023.chapter-4.text-2">
                Meanwhile, 21% of young people in low-income countries say they don’t have a go-to
                info source at all.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
